import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useFormikContext } from "formik";
import useDebounce from "hooks/useDebounce";
import { convertFileUrlsToFiles, parseAddressComponents } from "utils";
import "styles/AddressAutoComplete.css";
import { InputField } from ".";

// Static library list
const libraries = ["places"];

function AddressAutocomplete({
  loadPropertyImages = false,
  fieldNames,
  ...props
}) {
  const prevAddressRef = useRef("");
  const componentRef = useRef();
  const [suggestions, setSuggestions] = useState([]);
  const autocompleteService = useRef(null);
  const { setFieldValue, values, errors } = useFormikContext();
  const debounceAddress = useDebounce(values[fieldNames.address]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (isLoaded) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (!componentRef.current) return;

    function handleClickOutside(event) {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        if (suggestions.length > 0) {
          setSuggestions([]);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestions.length]);

  useEffect(() => {
    if (!autocompleteService.current) return;

    const address = values[fieldNames.address];
    if (
      address &&
      !errors[fieldNames.address] &&
      prevAddressRef.current !== address
    ) {
      handleInputChange(address);
    }
  }, [debounceAddress, errors[fieldNames.address]]);

  const handleInputChange = useCallback((input) => {
    autocompleteService.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: "us" },
        types: ["address"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const placeSuggestions = predictions.map((place) => ({
            id: place.place_id,
            value: place.description,
            label: place.description,
          }));
          setSuggestions(placeSuggestions);
        } else {
          setSuggestions([]);
        }
      }
    );
  }, []);

  const handleSuggestionClick = (placeId) => {
    setSuggestions([]);
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService.getDetails(
      {
        placeId: placeId,
      },
      async (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const { address, state, city, zip } = parseAddressComponents(
            place.address_components
          );

          setFieldValue(fieldNames.address, address);
          setFieldValue(fieldNames.state, state);
          setFieldValue(fieldNames.city, city);
          setFieldValue(fieldNames.zip, zip);
          prevAddressRef.current = address;

          if (loadPropertyImages) {
            const urls = getStreetViewImages(address);
            const files = await convertFileUrlsToFiles(urls, true);
            setFieldValue(fieldNames.propertyImage, files);
          }
        }
      }
    );
  };

  const getStreetViewImages = (address) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
    const baseUrl = "https://maps.googleapis.com/maps/api/streetview";
    const size = "600x400"; // Size of each image in pixels
    const headings = [0, 90, 180, 270]; // Different angles for images
    const pitch = 0; // Default pitch (horizontal view)

    // Create an array of URLs, each with a different heading
    const urls = headings.map((heading) => {
      const params = new URLSearchParams({
        size,
        location: address,
        heading: heading.toString(),
        pitch: pitch.toString(),
        key: apiKey,
      });

      return `${baseUrl}?${params.toString()}`;
    });

    return urls;
  };

  return (
    <div ref={componentRef} className="relative w-full">
      <InputField {...props} />
      {!props.disabled && (
        <div className=" absolute w-full left-0 top-full z-50 pt-2">
          {suggestions.length > 0 && debounceAddress && (
            <ul
              className=" bg-white dark:bg-bgsecondary rounded-md border no-scrollbar"
              style={{ scrollbarColor: "red", scrollbarWidth: 0 }}
            >
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion.id)}
                  className="border-b px-4 py-2 dark:text-white last:border-b-0 cursor-pointer"
                >
                  {suggestion.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
